/**
 * Auto-Publish Poller Hook
 *
 * Polling-based replacement for SSE-driven auto-publish detection.
 * Monitors content with status='completed' AND publish_status='pending'
 * and triggers WordPress publishing.
 *
 * Polling Strategy:
 * - Active tab: Poll every 5 seconds
 * - Hidden tab: Poll every 30 seconds (visibility-aware)
 *
 * Flow:
 * 1. Poll GET /blog-creation/content for pending items
 * 2. Filter for status='completed' AND publish_status='pending'
 * 3. For each pending item (that isn't already being processed):
 *    a. Fetch full content with content_json
 *    b. Generate HTML via generateHTMLFromRTE(content_json)
 *    c. Call blogCreationService.autoPublish(id, html)
 * 4. Backend updates publish_status to 'publishing' -> 'published' or 'failed'
 *
 * Key Principle: Frontend generates HTML, backend tracks state.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { generateHTMLFromRTE } from "@/components/blog-templates";
import { blogCreationService } from "@/services/blogCreationService";
import { useTenantStore } from "@/stores/useTenantStore";
import type { Content } from "@/components/BlogCreationCommandCenter/types";

const LOG_PREFIX = "[AUTO_PUBLISH_POLLER]";

// Polling intervals in milliseconds
const ACTIVE_TAB_INTERVAL = 15000; // 15 seconds when tab is visible
const HIDDEN_TAB_INTERVAL = 60000; // 60 seconds when tab is hidden

interface AutoPublishPollerReturn {
	/** Whether the poller is actively polling */
	isPolling: boolean;
	/** Number of content items currently pending auto-publish */
	pendingCount: number;
	/** Timestamp of last successful poll */
	lastChecked: Date | null;
}

/**
 * Hook that manages auto-publishing via polling.
 *
 * Mount this hook once at the app root level. It will:
 * - Poll the content list API at regular intervals
 * - Detect items with status='completed' AND publish_status='pending'
 * - Generate HTML and call autoPublish endpoint for pending items
 * - Adjust polling frequency based on tab visibility
 *
 * @example
 * ```tsx
 * // In App.tsx or root layout
 * function App() {
 *   const { isPolling, pendingCount, lastChecked } = useAutoPublishPoller();
 *
 *   return (
 *     <div>
 *       {pendingCount > 0 && <span>Auto-publishing: {pendingCount} pending</span>}
 *       {children}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAutoPublishPoller(): AutoPublishPollerReturn {
	const queryClient = useQueryClient();
	const { currentTenant } = useTenantStore();

	// Delay initial poll to avoid request burst on page mount.
	// The content list (useContentList) loads first; this poller starts 10s later.
	const [mountReady, setMountReady] = useState(false);
	useEffect(() => {
		const timer = setTimeout(() => setMountReady(true), 10_000);
		return () => clearTimeout(timer);
	}, []);

	// Track which content IDs are currently being processed
	// Prevents concurrent processing of the same content
	const processingRef = useRef<Set<string>>(new Set());

	// Track last checked timestamp
	const [lastChecked, setLastChecked] = useState<Date | null>(null);

	// Track tab visibility for adaptive polling
	const [isTabVisible, setIsTabVisible] = useState(!document.hidden);

	// Handle visibility change
	useEffect(() => {
		const handleVisibilityChange = () => {
			const visible = !document.hidden;
			setIsTabVisible(visible);
			console.log(
				`${LOG_PREFIX} Tab visibility changed: ${visible ? "visible" : "hidden"}`,
			);
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, []);

	/**
	 * Process a single content item for auto-publish
	 */
	const processContent = useCallback(
		async (
			contentId: string,
			publishImmediately: boolean,
			operationTenant: string | null,
		): Promise<void> => {
			// Check if already processing this content
			if (processingRef.current.has(contentId)) {
				console.log(
					`${LOG_PREFIX} Content ${contentId} already being processed, skipping`,
				);
				return;
			}

			try {
				// Mark as processing to prevent concurrent attempts
				processingRef.current.add(contentId);

				console.log(
					`${LOG_PREFIX} Starting auto-publish for content: ${contentId}`,
				);

				// Show "Publishing..." toast
				toast.info(
					publishImmediately
						? `Publishing: ${contentId.slice(0, 8)}...`
						: `Syncing draft: ${contentId.slice(0, 8)}...`,
					{
						description: publishImmediately
							? "Auto-publishing to WordPress"
							: "Saving to WordPress as draft",
						duration: 2000,
					},
				);

				// Step 1: Fetch full content with content_json
				console.log(`${LOG_PREFIX} Fetching content details...`);
				const contentDetail = await blogCreationService.get(contentId, operationTenant);

				// Verify content is ready for auto-publish
				if (contentDetail.status !== "completed") {
					console.log(
						`${LOG_PREFIX} Content ${contentId} status is '${contentDetail.status}', not 'completed'. Skipping.`,
					);
					return;
				}

				if (contentDetail.publish_status !== "pending") {
					console.log(
						`${LOG_PREFIX} Content ${contentId} publish_status is '${contentDetail.publish_status}', not 'pending'. Skipping.`,
					);
					return;
				}

				if (!contentDetail.content_json) {
					throw new Error(
						"Content JSON not available - content may not be fully generated",
					);
				}

				console.log(
					`${LOG_PREFIX} Content fetched, content_json has ${contentDetail.content_json.content?.length ?? 0} nodes`,
				);

				// Step 2: Generate HTML from content_json. Classify links against the
				// tenant host bound to THIS poll operation (not a stale closure), so a
				// mid-flight workspace switch can never render tenant A content with
				// tenant B's host.
				console.log(`${LOG_PREFIX} Generating HTML from content_json...`);
				const htmlContent = generateHTMLFromRTE(contentDetail.content_json, operationTenant);
				console.log(
					`${LOG_PREFIX} HTML generated, length: ${htmlContent.length} characters`,
				);

				// Step 3: Call auto-publish endpoint (backend handles state transition)
				console.log(`${LOG_PREFIX} Calling auto-publish endpoint...`);
				const publishResponse = await blogCreationService.autoPublish(
					contentId,
					htmlContent,
					publishImmediately,
					operationTenant,
				);
				if (publishResponse.skipped) {
					console.log(
						`${LOG_PREFIX} Auto-publish skipped: ${publishResponse.skip_reason ?? "unknown"}`,
					);
					toast.warning("Auto-publish held for human QA", {
						description:
							publishResponse.skip_reason === "pending_human_qa"
								? "A reviewer must approve this draft before auto-publish continues."
								: "The backend skipped this auto-publish attempt.",
						duration: 5000,
					});
					queryClient.invalidateQueries({
						queryKey: ["blog-creation-content"],
						exact: false,
					});
					queryClient.invalidateQueries({
						queryKey: ["blog-creation-content-item", contentId],
					});
					return;
				}
				console.log(
					`${LOG_PREFIX} Auto-publish completed: ${publishResponse.post_url}`,
				);

				// Show success toast with link to view post
				const title = contentDetail.title || "Article";
				if (publishImmediately) {
					toast.success(`Published: ${title}`, {
						description: "Successfully published to WordPress",
						action: publishResponse.post_url
							? {
									label: "View Post",
									onClick: () => window.open(publishResponse.post_url, "_blank"),
								}
							: undefined,
						duration: 5000,
					});
				} else {
					toast.success(`Synced to WordPress (Draft): ${title}`, {
						description: "Saved to WordPress as draft",
						action: publishResponse.post_url
							? {
									label: "View Draft",
									onClick: () => window.open(publishResponse.post_url, "_blank"),
								}
							: undefined,
						duration: 5000,
					});
				}

				// Invalidate queries to refresh UI
				queryClient.invalidateQueries({
					queryKey: ["blog-creation-content"],
					exact: false,
				});
				queryClient.invalidateQueries({
					queryKey: ["blog-creation-content-item", contentId],
				});
				// Also refresh the Content Planner table/calendar so the just-
				// published item moves from "Active" to "Done" live. The planner's
				// own poll stops at status='completed', so without this the publish
				// (completed -> published) isn't reflected until a manual refresh.
				queryClient.invalidateQueries({
					queryKey: ["content-planner", "calendar"],
					exact: false,
				});
			} catch (error) {
				const errorMessage =
					error instanceof Error ? error.message : "Unknown error";
				console.error(
					`${LOG_PREFIX} Auto-publish failed for ${contentId}:`,
					errorMessage,
				);

				// Show error toast
				toast.error("Auto-publish failed", {
					description: errorMessage,
					duration: 6000,
				});

				// Note: Backend will handle marking as failed if the request fails
			} finally {
				// Remove from processing set
				processingRef.current.delete(contentId);
			}
		},
		[queryClient],
	);

	/**
	 * Process pending items from poll response
	 */
	const processPendingItems = useCallback(
		(items: Content[], operationTenant: string | null) => {
			// Filter for items that need auto-publishing
			const pendingItems = items.filter(
				(item) =>
					item.status === "completed" &&
					item.publish_status === "pending" &&
					!processingRef.current.has(item.id),
			);

			if (pendingItems.length > 0) {
				console.log(
					`${LOG_PREFIX} Found ${pendingItems.length} items pending auto-publish`,
				);

				// Process each pending item under the operation-bound tenant host.
				for (const item of pendingItems) {
					processContent(
						item.id,
						item.wordpress_publish_mode === "publish" || item.publish_immediately === true,
						operationTenant,
					);
				}
			}
		},
		[processContent],
	);

	// Polling query using React Query
	const { data, isLoading } = useQuery({
		queryKey: ["auto-publish-poll", currentTenant],
		queryFn: async () => {
			// Bind the tenant host to this poll batch. This query is keyed by
			// `currentTenant`, so the items it returns belong to this exact tenant;
			// capturing it here (rather than reading a live/closure value later)
			// keeps fetch + HTML conversion + auto-publish on one tenant even if the
			// operator switches workspaces mid-flight.
			const operationTenant = currentTenant ?? null;

			// Don't log every poll - only log meaningful events
			const response = await blogCreationService.list(
				{
					// Auto-publish only starts after generation completes.
					status: "completed",
					page_size: 100, // Reasonable limit
				},
				undefined,
				operationTenant,
			);

			setLastChecked(new Date());

			// Process any pending items found
			if (response.items) {
				processPendingItems(response.items, operationTenant);
			}

			return response;
		},
		// Wait 10s after mount before first poll to avoid request burst
		enabled: !!currentTenant && mountReady,
		// Adaptive polling interval based on tab visibility
		refetchInterval: isTabVisible ? ACTIVE_TAB_INTERVAL : HIDDEN_TAB_INTERVAL,
		// Keep previous data to avoid UI flicker
		placeholderData: (previousData) => previousData,
		// Don't refetch on window focus - we have our own interval
		refetchOnWindowFocus: false,
		// Keep data fresh
		staleTime: 0,
		retry: 1, // Limit retries to avoid pileup
	});

	// Calculate pending count from current data
	const pendingCount =
		data?.items?.filter(
			(item) =>
				item.status === "completed" &&
				item.publish_status === "pending" &&
				!processingRef.current.has(item.id),
		).length ?? 0;

	return {
		isPolling: !isLoading && !!currentTenant,
		pendingCount,
		lastChecked,
	};
}

export default useAutoPublishPoller;
